// Log entry helpers — the per-terminal log list uses these to build
// rows and to dedupe incremental renders.
//
// logKey() builds the dedupe key (ts + event) — see renderTerminalLogs
// in src/render/terminalLogs.ts for how it's used.
//
// terminalLogRow() builds one per-terminal row's HTML (ts + event +
// logval). terminalGlobalLogRow() builds one global-log table row
// (ts + terminal name/location + event + logval).
// terminalNotificationLogRow() is a near-twin of
// terminalGlobalLogRow() that adds a click-to-dismiss affordance
// (data-notify-id, cursor-pointer, hover, transition) so the
// dashboard notify panel's delegated click handler can find and
// dismiss rows. Keeping it as a dedicated helper (rather than
// post-injecting data-notify-id with .replace() in renderNotify)
// means the click contract is owned here, in the same file as the
// row shape, so the two can't silently desync the way they did
// before. All three call escapeHtml() from ./html.ts so we have a
// single source of truth for safe string interpolation.
//
// NOTE: these are technically "view" helpers, not pure utilities,
// because they produce HTML. But they have no DOM dependency (they
// only return strings) and they're used by exactly one or two
// consumers each, so they live here to keep the render layer thin.

import { escapeHtml } from './html';
import { displayTerminalName } from './display';
import type { LogEntry, Terminal } from '../types';

export function logKey(l: LogEntry | null | undefined): string {
  return String(l?.ts != null ? l.ts : '') + '|' + String(l?.event != null ? l.event : '');
}

// One per-terminal row, matching the old per-terminal styling
// (rounded card on a soft gray background, with a ts column + event
// column). Returns an HTML string — caller inserts into the DOM.
//
// (Phase 7: bundle had drifted to bg-gray-700 + an extra wrapping span
// around the timestamp. Inline ships with bg-gray-100 and a single
// <span class="font-mono text-gray-400 w-38"> for the ts. Reverted to
// the inline shape so the bundle is behaviour-preserving.)
export function terminalLogRow(l: LogEntry | null | undefined): string {
  const ts = escapeHtml(l?.ts ?? '');
  const time = escapeHtml((l?.ts)?.split(' ')[1] ?? '');
  const date = escapeHtml((l?.ts)?.split(' ')[0] ?? '');
  const event = escapeHtml(l?.event ?? '');
  const lval = escapeHtml(l?.logval ?? '');
  // Original inline code did ` (l && l.type) ? ' ' + l.type : ''` and
  // did NOT sanitise. That allows a class attribute injection via a
  // hostile backend. We sanitise to class-safe characters only —
  // defence-in-depth — and still match the inline visual when the
  // type field is a clean identifier.
  const rawType = l?.type;
  const type = (typeof rawType === 'string' && rawType.length)
    ? ' ' + rawType.replace(/[^a-zA-Z0-9_-]/g, '')
    : '';
  return `<div class="flex gap-x-3 text-xs bg-gray-100 rounded-2xl px-4 justify-between py-2 ${type}">

    <span class="w-[69px] font-mono text-gray-600">${time}</span>
    <div class="flex justify-between" style="width: calc( 100% - 75px );">
    <span class="w-[150px]">${event}</span>
    <span class="logvalue">${lval}</span></div>
  </div>`;
}

// One global-log table row. `t` is the looked-up terminal (already
// resolved by the caller via `terminals[l.mid]`), or null if the
// terminal is missing / the entry has no mid. The terminal label
// column falls back to a dash in that case so the row still renders.
//
// The row mirrors the inline index.php renderGlobalLogs cell shape
// (4 columns: ts, location+name, event, logval). The Activity Logs page
// in admin/shell.php declares `<div id="global-logs-body">` (no table
// wrapper — see shell.php for the layout) with TIME / TERMINAL / EVENT /
// DETAIL column headers above, so rows render as flex <div>s that
// visually align with the header strip. logval rides in a 4th column
// to match the inline behaviour that shipped to production.
// shape so the bundle is a behaviour-preserving port of the inline.)
export function terminalGlobalLogRow(
  l: LogEntry | null | undefined,
  t: Terminal | null | undefined,
): string {

  const tid = l?.mid ?? -1;
  const ts = escapeHtml(l?.ts ?? '');
  const time = escapeHtml((l?.ts)?.split(' ')[1] ?? '');
  const date = escapeHtml((l?.ts)?.split(' ')[0] ?? '');
  const midRaw = escapeHtml(t?.mid ?? '{mid}');
  const midSafe = escapeHtml(midRaw);
  const loc = escapeHtml(t?.location ?? '{location}');
  const hwid = escapeHtml(t?.location ?? '{location}');
  const name = escapeHtml(t?.name ?? '\u2014'); // em-dash fallback when no terminal
  const event = escapeHtml(l?.event ?? '');
  const lvalRaw = l?.logval != null ? String(l.logval) : '';
  const lval = escapeHtml(lvalRaw);
  // Sanitise the type modifier the same way terminalLogRow does.
  const rawType = l?.type;
  const type = (typeof rawType === 'string' && rawType.length)
      ? ' ' + rawType.replace(/[^a-zA-Z0-9_-]/g, '')
      : '';
  // style="width: calc( 100% - 75px );">
  return `<div class="grid grid-cols-6 gap-x-1 text-xs bg-gray-100 md:rounded-2xl md:px-4 sm:px-2 px-1 justify-between py-2 ${type}">

    <div class="col-span-1 font-mono text-gray-500">
      <span class="hidden sm:inline-block">${date}</span>
      <span class="text-gray-800 ">${time}</span>
    </div>
    <div class="col-span-2 cursor-pointer" alt="${midSafe}" onclick="openTerminal(${tid});">
      <span>${name} </span>
      <div class="inline-block">@${loc}</div>
    </div>
    <div class="col-span-2 ">${event}</div>
    <div class="font-mono text-right">${lval}</div>
  </div>`;

  // mid and tMid are kept around so future revisions can wire up the
  // "click terminal id to jump to detail" link the bundle's earlier
  // <div> version had. Inline doesn't have that link yet either.

}

// One notification-panel row. Same visual columns as
// terminalGlobalLogRow() (date+time / terminal / event / logval) so
// the two look identical, but the row is itself a click target:
// the delegated click handler in installNotifyClickHandler()
// (admin/src/render.ts) reads data-notify-id off the row and calls
// /api/notifyRead. The cursor-pointer / hover / transition classes
// are applied to the WHOLE row (not a separate child span) so the
// affordance is the row itself, and the `title` attribute explains
// it to a hovering user.
//
// Archived (already-read) rows get the `archived` class on the
// parent: a styling hook that drops the hover state and dims the
// row, since there's nothing for the user to do. The /api/notify
// endpoint signals archived rows with a non-null `readon` field
// (a row that has already been moved from `notify` to
// `notify_arch`). Click-to-dismiss is suppressed for archived
// rows: the delegated click handler still finds the row
// (data-notify-id is set) but bails out when the row has the
// `archived` class, so a stray click on a stale row can't trigger
// a redundant /api/notifyRead.
//
// The terminal cell keeps its own onclick="openTerminal(mid)"
// link — same as the global-log row — and stops propagation so a
// click on the terminal name doesn't also count as a dismiss. That
// mirrors how the Activity Logs page behaves: click the row, nothing
// happens; click the terminal, open the modal. Here it's click the
// row, dismiss; click the terminal, open the modal.
//
// `id` is the notify-table PK (from /api/notify's `id` field). When
// id is missing or non-positive the row is still rendered, but
// without data-notify-id — the click handler will ignore it (it
// requires a finite id > 0) so the row is read-only in that case.
export function terminalNotificationLogRow(
  l: LogEntry | null | undefined,
  t: Terminal | null | undefined,
): string {

  const tid = l?.mid ?? -1;
  const idRaw = l?.id;
  const idNum = (typeof idRaw === 'number' && Number.isFinite(idRaw))
    ? idRaw
    : (typeof idRaw === 'string' && idRaw.length && /^\d+$/.test(idRaw) ? Number(idRaw) : 0);
  const idAttr = idNum > 0 ? ` data-notify-id="${idNum}"` : '';
  const titleAttr = idNum > 0 ? ' title="Click to dismiss"' : '';
  // Read state: the server signals "already archived" with a
  // non-null `readon` field. Live rows omit it (undefined).
  // Archived rows drop the click affordance and pick up the
  // `archived` class — the same class the click handler uses
  // to short-circuit /api/notifyRead for stale rows.
  const readon = (l && typeof (l as { readon?: unknown }).readon === 'string')
    ? (l as { readon?: string }).readon
    : null;
  const isArchived = readon !== null && readon !== '';

  // Interactive affordance (cursor-pointer / hover / transition)
  // rides on the WHOLE row, not a child span. Archived rows
  // drop the affordance entirely. A missing-id row also falls
  // back to a non-interactive static row (no dismiss button
  // wired up), but does NOT get the `archived` class — it's
  // just a read-only pending row.
  const interactiveCls = !isArchived && idNum > 0
    ? ' cursor-pointer transition-colors'
    : '';
  const archivedCls = isArchived ? ' archived' : '';

  const ts = escapeHtml(l?.ts ?? '');
  const time = escapeHtml((l?.ts)?.split(' ')[1] ?? '');
  const date = escapeHtml((l?.ts)?.split(' ')[0] ?? '');
  const midSafe = escapeHtml(t?.mid ?? '{mid}');
  const loc = escapeHtml(t?.location ?? '{location}');
  const name = escapeHtml(t?.name ?? '\u2014'); // em-dash fallback when no terminal
  const event = escapeHtml(l?.event ?? '');
  const lvalRaw = l?.logval != null ? String(l.logval) : '';
  const lval = escapeHtml(lvalRaw);
  // Sanitise the type modifier the same way terminalGlobalLogRow does.
  const rawType = l?.type;
  const type = (typeof rawType === 'string' && rawType.length)
      ? ' ' + rawType.replace(/[^a-zA-Z0-9_-]/g, '')
      : '';
  // Whole row is the click target — no separate child span.
  // interactiveCls and archivedCls ride on the parent. stopPropagation
  // on the terminal cell keeps "click to open" and "click to
  // dismiss" from fighting each other.
  return `<div class="gap-x-1 relative  text-xs bg-gray-100 rounded-2xl p-2 sm:px-2 px-3 justify-between ${archivedCls} ${type}">

    <div class="col-span- font-mono text-gray-500">
      <span class="hidden sm:inline-block">${date}</span>
      <span class="text-gray-800 ">${time}</span>
    </div>
    <div class="col-span- inline-block cursor-pointer" alt="${midSafe}" onclick="event.stopPropagation(); openTerminal(${tid});">
      <span class="hover:underline">${name} </span>
      <div>@${loc}</div>
    </div>
    <div class="col-span- ">${event}</div>
    <div class="font-mono text-right">${lval}</div>
    <span ${idAttr}${titleAttr} class="${interactiveCls} unread absolute top-0 right-0 -mt-2 -mr-2 flex size-10">
      <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
      <span class="relative inline-flex size-5 mt-2.5 ml-2.5 rounded-full bg-red-500"></span>
    </span>
  </div>`;
}
